import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import PublicationForm from '@web/src/features/publication/publication-form/publication-form'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { PublicationInfo } from '@web/src/lib/fragments'
import i18n from '@web/src/lib/intl/i18n'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  EditPublicationByIdQuery,
  EditPublicationByIdQueryVariables,
  UpdatePublicationInput,
  UpdatePublicationMutation,
  UpdatePublicationMutationVariables,
} from 'types/graphql'

const EDIT_PUBLICATION_QUERY: TypedDocumentNode<EditPublicationByIdQuery, EditPublicationByIdQueryVariables> = gql`
  query EditPublicationById($publicationIdOrSlug: String!) {
    publication: publication(publicationIdOrSlug: $publicationIdOrSlug) {
      ...PublicationInfo
    }
    publicationTags {
      id
      name
    }
  }
  ${PublicationInfo}
`

const UPDATE_PUBLICATION_MUTATION: TypedDocumentNode<UpdatePublicationMutation, UpdatePublicationMutationVariables> =
  gql`
    mutation UpdatePublicationMutation($publicationIdOrSlug: String!, $input: UpdatePublicationInput!) {
      updatePublication(publicationIdOrSlug: $publicationIdOrSlug, input: $input) {
        ...PublicationInfo
      }
    }
    ${PublicationInfo}
  `

export const Route = createFileRoute('/(auth)/(main-layout)/publication/$publicationIdOrSlug/edit')({
  component: EditPublicationPage,
  head: () => {
    const t = i18n.t.bind(i18n)
    return seo({
      title: `${t('Edit Publication')} | ${env.VITE_APP_NAME}`,
      description: t('Edit a Publication entry'),
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    })
  },
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, EDIT_PUBLICATION_QUERY, {
      variables: { publicationIdOrSlug: params.publicationIdOrSlug },
    })
    return { queryRef }
  },
})

function EditPublicationPage() {
  const { t } = useTranslation()
  const { publicationIdOrSlug } = Route.useParams()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const navigate = useNavigate()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<EditPublicationByIdQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const [updatePublication] = useMutation(UPDATE_PUBLICATION_MUTATION, {
    onCompleted: (result) => {
      toast.success(t('Publication updated'))
      navigate({
        to: '/publication/$publicationIdOrSlug',
        params: { publicationIdOrSlug: result.updatePublication.slug ?? result.updatePublication.id } as never,
      })
      setShowSpinnerValue(false)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setShowSpinnerValue(false)
    },
  })

  const onSave = (
    input: UpdatePublicationInput,
    publicationIdParam?: NonNullable<EditPublicationByIdQuery['publication']>['id']
  ) => {
    setShowSpinnerValue(true)
    updatePublication({
      variables: { publicationIdOrSlug: publicationIdParam || publicationIdOrSlug, input },
    })
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!(data?.publication && data?.publicationTags)) {
    return <ErrorAlert error={new Error(t('Publication or publication tags not found'))} />
  }

  return (
    <div className="mx-auto w-full max-w-2xl pt-4">
      <div className="flex w-full flex-col items-stretch gap-4">
        <h1 className="w-full text-center font-bold text-3xl text-foreground">{t('Edit Publication')}</h1>
        <PublicationForm
          onSave={onSave}
          publication={data.publication}
          publicationTags={data.publicationTags}
          showSpinner={showSpinner}
        />
      </div>
    </div>
  )
}
